/**
 * Card Issuer Component
 * Issue virtual or physical prepaid cards to members via Marqeta
 * Trust Marketplace - Payment Account Management
 */

import React, { useState, useEffect } from 'react';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { LoadingSpinner } from '../ui/LoadingSpinner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Textarea } from '../ui/textarea';
import { marqetaCardService, MERCHANT_CATEGORIES, type MarqetaCardResponse } from '@/services/marqeta/cardIssuance';
import { useAppTranslation } from '@/contexts/TranslationContext';
import {
  CreditCard,
  DollarSign,
  Shield,
  AlertCircle,
  CheckCircle,
  Loader2,
  Eye,
  EyeOff,
  Plus,
  Minus
} from 'lucide-react';

interface CardIssuerProps {
  memberId: number;
  memberName: string;
  isOpen: boolean;
  onClose: () => void;
  onCardIssued?: (card: MarqetaCardResponse) => void;
}

const CARD_TYPES = [
  { value: 'VIRTUAL', label: 'Virtual Card (Instant)', description: 'Digital card for online purchases' },
  { value: 'PHYSICAL', label: 'Physical Card (5-7 days)', description: 'Physical card mailed to member' }
];

const PRESET_CATEGORIES = [
  { name: 'Grocery', codes: MERCHANT_CATEGORIES.GROCERY },
  { name: 'Gas & Transportation', codes: MERCHANT_CATEGORIES.GAS.concat(MERCHANT_CATEGORIES.TRANSPORTATION) },
  { name: 'Healthcare & Pharmacy', codes: MERCHANT_CATEGORIES.HEALTHCARE.concat(MERCHANT_CATEGORIES.PHARMACY) },
  { name: 'Restaurants', codes: MERCHANT_CATEGORIES.RESTAURANTS },
  { name: 'Utilities', codes: MERCHANT_CATEGORIES.UTILITIES }
];

export const CardIssuer: React.FC<CardIssuerProps> = ({
  memberId,
  memberName,
  isOpen,
  onClose,
  onCardIssued
}) => {
  const { t } = useAppTranslation();
  const [issuing, setIssuing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [issuedCard, setIssuedCard] = useState<MarqetaCardResponse | null>(null);

  const [cardRequest, setCardRequest] = useState({
    cardType: 'VIRTUAL' as 'VIRTUAL' | 'PHYSICAL',
    loadAmount: 100,
    dailyLimit: 50,
    weeklyLimit: 200,
    monthlyLimit: 500,
    allowedCategories: [] as string[],
    restrictedCategories: [] as string[]
  });

  const [showAdvanced, setShowAdvanced] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      // Reset form when dialog closes
      setSuccess(false);
      setError(null);
      setIssuedCard(null);
    }
  }, [isOpen]);

  const handleIssueCard = async () => {
    try {
      setIssuing(true);
      setError(null);

      // Validate
      if (cardRequest.loadAmount <= 0) {
        setError('Load amount must be greater than 0');
        return;
      }

      console.log('🔵 Issuing card with request:', {
        memberId,
        cardType: cardRequest.cardType,
        cardholderName: memberName,
        loadAmount: cardRequest.loadAmount
      });

      const card = await marqetaCardService.issueCard({
        memberId,
        cardType: cardRequest.cardType,
        cardholderName: memberName,
        loadAmount: cardRequest.loadAmount,
        spendingLimits: {
          daily: cardRequest.dailyLimit,
          weekly: cardRequest.weeklyLimit,
          monthly: cardRequest.monthlyLimit
        },
        allowedCategories: cardRequest.allowedCategories.length > 0 ? cardRequest.allowedCategories : undefined,
        restrictedCategories: cardRequest.restrictedCategories.length > 0 ? cardRequest.restrictedCategories : undefined
      });

      console.log('✅ Card issued successfully:', card);

      setIssuedCard(card);
      setSuccess(true);

      console.log('✅ Success state set, will auto-close in 3 seconds');

      // Auto-close after 3 seconds and THEN notify parent
      setTimeout(() => {
        console.log('🔵 Auto-closing modal and notifying parent');
        if (onCardIssued) {
          onCardIssued(card);
        }
        onClose();
      }, 3000);
    } catch (err) {
      console.error('❌ Error issuing card:', err);
      setError(err instanceof Error ? err.message : 'Failed to issue card');
    } finally {
      setIssuing(false);
    }
  };

  const toggleCategory = (codes: string[], isAllowed: boolean) => {
    if (isAllowed) {
      const currentCodes = new Set(cardRequest.allowedCategories);
      const hasAll = codes.every(code => currentCodes.has(code));

      if (hasAll) {
        // Remove all
        setCardRequest({
          ...cardRequest,
          allowedCategories: cardRequest.allowedCategories.filter(code => !codes.includes(code))
        });
      } else {
        // Add all
        setCardRequest({
          ...cardRequest,
          allowedCategories: [...new Set([...cardRequest.allowedCategories, ...codes])]
        });
      }
    } else {
      const currentCodes = new Set(cardRequest.restrictedCategories);
      const hasAll = codes.every(code => currentCodes.has(code));

      if (hasAll) {
        // Remove all
        setCardRequest({
          ...cardRequest,
          restrictedCategories: cardRequest.restrictedCategories.filter(code => !codes.includes(code))
        });
      } else {
        // Add all
        setCardRequest({
          ...cardRequest,
          restrictedCategories: [...new Set([...cardRequest.restrictedCategories, ...codes])]
        });
      }
    }
  };

  const isCategorySelected = (codes: string[], isAllowed: boolean): boolean => {
    const currentCodes = isAllowed ? cardRequest.allowedCategories : cardRequest.restrictedCategories;
    return codes.every(code => currentCodes.includes(code));
  };

  if (success && issuedCard) {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{t('financial.card_issued_successfully', 'Card Issued Successfully!')}</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="text-center p-6 bg-green-50 rounded-lg">
              <CheckCircle className="w-16 h-16 text-green-600 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                {issuedCard.card_type} Card Issued
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                Card ending in {issuedCard.card_number_last4} has been issued to {memberName}
              </p>

              <div className="space-y-2 text-left bg-white dark:bg-gray-800 p-4 rounded border">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600 dark:text-gray-300">{t('financial.initial_balance', 'Initial Balance:')}</span>
                  <span className="font-semibold">${issuedCard.current_balance.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600 dark:text-gray-300">{t('financial.card_network', 'Card Network:')}</span>
                  <span className="font-semibold">{issuedCard.card_network}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600 dark:text-gray-300">{t('financial.status', 'Status:')}</span>
                  <Badge variant="outline" className="bg-green-50 text-green-700">
                    {issuedCard.status}
                  </Badge>
                </div>
              </div>
            </div>

            <Button onClick={() => {
              if (onCardIssued && issuedCard) {
                onCardIssued(issuedCard);
              }
              onClose();
            }} className="w-full">
              {t('financial.done', 'Done')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Issue Card to {memberName}</DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-2">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-red-900">{t('financial.error_issuing_card', 'Error Issuing Card')}</p>
                <p className="text-sm text-red-700">{error}</p>
              </div>
            </div>
          )}

          {/* Card Type */}
          <div>
            <Label>{t('financial.card_type', 'Card Type *')}</Label>
            <div className="grid grid-cols-2 gap-3 mt-2">
              {CARD_TYPES.map((type) => (
                <Card
                  key={type.value}
                  className={`p-4 cursor-pointer transition-all ${cardRequest.cardType === type.value
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300'
                    }`}
                  onClick={() => setCardRequest({ ...cardRequest, cardType: type.value as 'VIRTUAL' | 'PHYSICAL' })}
                >
                  <div className="flex items-start gap-3">
                    <CreditCard className={`w-5 h-5 ${cardRequest.cardType === type.value ? 'text-blue-600' : 'text-gray-400'
                      }`} />
                    <div>
                      <p className="font-medium text-sm">{type.label}</p>
                      <p className="text-xs text-gray-600 mt-1">{type.description}</p>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>

          {/* Load Amount */}
          <div>
            <Label htmlFor="loadAmount">{t('financial.initial_load_amount', 'Initial Load Amount *')}</Label>
            <div className="relative mt-1">
              <DollarSign className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
              <Input
                id="loadAmount"
                type="number"
                min="1"
                step="0.01"
                value={cardRequest.loadAmount}
                onChange={(e) => setCardRequest({ ...cardRequest, loadAmount: parseFloat(e.target.value) || 0 })}
                className="pl-10"
              />
            </div>
            <p className="text-xs text-gray-600 mt-1">
              {t('financial.amount_to_load_onto_the', 'Amount to load onto the card initially')}
            </p>
          </div>

          {/* Spending Limits */}
          <div>
            <Label>{t('financial.spending_limits', 'Spending Limits')}</Label>
            <div className="grid grid-cols-3 gap-3 mt-2">
              <div>
                <Label htmlFor="dailyLimit" className="text-xs">{t('financial.daily_limit', 'Daily Limit')}</Label>
                <div className="relative mt-1">
                  <DollarSign className="absolute left-2 top-2.5 w-3 h-3 text-gray-400" />
                  <Input
                    id="dailyLimit"
                    type="number"
                    min="0"
                    value={cardRequest.dailyLimit}
                    onChange={(e) => setCardRequest({ ...cardRequest, dailyLimit: parseFloat(e.target.value) || 0 })}
                    className="pl-7 text-sm"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="weeklyLimit" className="text-xs">{t('financial.weekly_limit', 'Weekly Limit')}</Label>
                <div className="relative mt-1">
                  <DollarSign className="absolute left-2 top-2.5 w-3 h-3 text-gray-400" />
                  <Input
                    id="weeklyLimit"
                    type="number"
                    min="0"
                    value={cardRequest.weeklyLimit}
                    onChange={(e) => setCardRequest({ ...cardRequest, weeklyLimit: parseFloat(e.target.value) || 0 })}
                    className="pl-7 text-sm"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="monthlyLimit" className="text-xs">{t('financial.monthly_limit', 'Monthly Limit')}</Label>
                <div className="relative mt-1">
                  <DollarSign className="absolute left-2 top-2.5 w-3 h-3 text-gray-400" />
                  <Input
                    id="monthlyLimit"
                    type="number"
                    min="0"
                    value={cardRequest.monthlyLimit}
                    onChange={(e) => setCardRequest({ ...cardRequest, monthlyLimit: parseFloat(e.target.value) || 0 })}
                    className="pl-7 text-sm"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Advanced Options */}
          <div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowAdvanced(!showAdvanced)}
              className="w-full flex items-center justify-between"
            >
              <span>{t('financial.advanced_options', 'Advanced Options')}</span>
              {showAdvanced ? <Minus className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
            </Button>

            {showAdvanced && (
              <div className="mt-4 space-y-4 border rounded-lg p-4">
                <div>
                  <Label className="flex items-center gap-2">
                    <Shield className="w-4 h-4" />
                    {t('financial.allowed_merchant_categories', 'Allowed Merchant Categories')}
                  </Label>
                  <p className="text-xs text-gray-600 mb-2">{t('financial.card_can_only_be_used', 'Card can only be used at these types of merchants')}</p>
                  <div className="flex flex-wrap gap-2">
                    {PRESET_CATEGORIES.map((category) => (
                      <Button
                        key={category.name}
                        variant={isCategorySelected(category.codes, true) ? "default" : "outline"}
                        size="sm"
                        onClick={() => toggleCategory(category.codes, true)}
                        className="text-xs"
                      >
                        {category.name}
                      </Button>
                    ))}
                  </div>
                </div>

                <div>
                  <Label className="flex items-center gap-2">
                    <AlertCircle className="w-4 h-4" />
                    {t('financial.restricted_merchant_categories', 'Restricted Merchant Categories')}
                  </Label>
                  <p className="text-xs text-gray-600 mb-2">{t('financial.card_cannot_be_used_at', 'Card cannot be used at these types of merchants')}</p>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      variant={isCategorySelected(MERCHANT_CATEGORIES.GAMBLING, false) ? "default" : "outline"}
                      size="sm"
                      onClick={() => toggleCategory(MERCHANT_CATEGORIES.GAMBLING, false)}
                      className="text-xs"
                    >
                      {t('financial.gambling', 'Gambling')}
                    </Button>
                    <Button
                      variant={isCategorySelected(MERCHANT_CATEGORIES.ALCOHOL, false) ? "default" : "outline"}
                      size="sm"
                      onClick={() => toggleCategory(MERCHANT_CATEGORIES.ALCOHOL, false)}
                      className="text-xs"
                    >
                      {t('financial.alcohol', 'Alcohol')}
                    </Button>
                    <Button
                      variant={isCategorySelected(MERCHANT_CATEGORIES.CASH_WITHDRAWAL, false) ? "default" : "outline"}
                      size="sm"
                      onClick={() => toggleCategory(MERCHANT_CATEGORIES.CASH_WITHDRAWAL, false)}
                      className="text-xs"
                    >
                      {t('financial.cash_withdrawal', 'Cash Withdrawal')}
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4 border-t">
            <Button variant="outline" onClick={onClose} className="flex-1" disabled={issuing}>
              {t('financial.cancel', 'Cancel')}
            </Button>
            <Button onClick={handleIssueCard} className="flex-1" disabled={issuing}>
              {issuing ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {t('financial.issuing_card', 'Issuing Card...')}
                </>
              ) : (
                <>
                  <CreditCard className="w-4 h-4 mr-2" />
                  {t('financial.issue_card', 'Issue Card')}
                </>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CardIssuer;
